<template>
  <IonApp class="flex flex-col">
    <IonHeader v-show="isHeaderVisible" class="ion-no-border">
      <div class="brand-gradient-h h-(--ion-safe-area-top)"></div>
      <AppNavbar :key="AppMenuKey" />
      <!-- Server-supplied alerts still carry remote icon names, so they opt out of Tabler.
           Locally-defined alerts (appRefreshAlert) set isTablerIcon themselves. -->
      <CustomInlineAlert
        v-for="alert in allAlerts"
        :title="alert.messageTitle"
        :message="alert.messageText"
        :color="alert.alertColor"
        :icon="alert.alertIcon ?? undefined"
        :isTablerIcon="alert.isTablerIcon ?? false"
        dismissable
      />
      <!-- usermgmt-8-7 (BR-UI-103) — per-session password-update nag, layout-level near the navbar -->
      <ThePasswordWarningBanner />
    </IonHeader>
    <AppFabMenu />
    <IonRouterOutlet
      id="main-content"
      :key="RouterOutletKey"
      class="relative flex-1 min-h-0"
      :swipe-gesture="isSwipeGestureEnabled"
    />
    <IonFooter class="ion-no-border app-footer-transparent">
      <div v-if="isFooterVisible">
        <TheAppFooter v-if="ShowAppFooter" />
        <FooterNavbar v-else :key="currentRouteKey" />
        <div class="brand-gradient-h pb-(--ion-safe-area-bottom)"></div>
      </div>
    </IonFooter>
    <AppExitAlert v-if="ShowAppExitAlert" />
    <CustomIonMenu menuId="navbar-menu">
      <template #menuContent>
        <NavbarMenu />
      </template>
    </CustomIonMenu>
  </IonApp>
</template>

<script lang="ts">
import CustomIonMenu from "@/components/ionic/CustomIonMenu.vue";
import AppFabMenu from "@/components/layout/AppFabMenu.vue";
import AppNavbar from "@/components/layout/AppNavbar.vue";
import FooterNavbar from "@/components/layout/FooterNavbar.vue";
import NavbarMenu from "@/components/layout/NavbarMenu.vue";
import TheAppFooter from "@/components/layout/TheAppFooter.vue";
import ThePasswordWarningBanner from "@/components/layout/ThePasswordWarningBanner.vue";
import AppExitAlert from "@/components/states/AppExitAlert.vue";
import CustomInlineAlert from "@/components/states/CustomInlineAlert.vue";
import { appRefreshAlert } from "@/constants/global";
import {
  initializeBackButton,
  installRouterDesyncSafetyNet,
  registerIonRouter,
} from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useAppUpdateStore } from "@/stores/core/appUpdateStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { IonApp, IonFooter, IonHeader, IonRouterOutlet, useIonRouter } from "@ionic/vue";
import { storeToRefs } from "pinia";
import { computed, defineComponent, inject, onBeforeMount, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "App",
  components: {
    IonApp,
    IonHeader,
    IonFooter,
    IonRouterOutlet,
    AppNavbar,
    TheAppFooter,
    FooterNavbar,
    CustomIonMenu,
    NavbarMenu,
    AppFabMenu,
    AppExitAlert,
    CustomInlineAlert,
    ThePasswordWarningBanner,
  },
  setup() {
    // Must run before any gotoPrevious() call — gotoPrevious falls back to raw router.back()
    // (the pre-fix desync behavior) until this registration happens.
    registerIonRouter(useIonRouter());
    // Browser-chrome back after a reload bypasses gotoPrevious() entirely; this watches
    // Ionic's routeInfo after each navigation and recovers the outlet when it desyncs.
    // useIonRouter() doesn't expose getCurrentRouteInfo, hence the raw navManager provide.
    installRouterDesyncSafetyNet(inject("navManager"));
    const currentRoute = useRoute();
    const appSettingsStore = useAppSettingsStore();
    const {
      AppUpdateRequired,
      AppMenuKey,
      RouterOutletKey,
      ShowAppFooter,
      IsNativePlatform,
      IsKeyboardOpen,
      IsAppFullscreen,
      ShowAppExitAlert,
    } = storeToRefs(appSettingsStore);
    const { initializeAppState, removeAppStateListeners } = appSettingsStore;
    const appUpdateStore = useAppUpdateStore();
    const { validateCurrentAppBundle } = appUpdateStore;
    const sessionStore = useSessionStore();
    const { UserInfo, UserAlerts, IsMustChangePasswordActive } = storeToRefs(sessionStore);

    const currentRouteKey = computed(() => {
      return currentRoute.fullPath;
    });

    const isHeaderVisible = computed(() => {
      return !IsAppFullscreen.value;
    });

    const isFooterVisible = computed(() => {
      // This footer is absolutely positioned over the router outlet at z-index 10
      // (.app-footer-transparent), and the outlet is its own z-index 0 stacking
      // context — so anything it renders paints over a page-level IonFooter and,
      // where pointer-events are re-enabled (the nav dock), swallows its taps.
      // Routes that hide the footer navigator own their bottom edge (the conduct
      // views put the question-response bar there), so stay out of their way.
      return !(IsAppFullscreen.value || IsKeyboardOpen.value);
    });

    // Task 0b-39: disable swipe-back on routes that opt-in via meta.overrideBackButton
    // (the 3 conduct views). Closes the 3 DEFER decisions from 0b-13's lifecycle audit.
    const isSwipeGestureEnabled = computed(() => {
      // usermgmt-8-2 — also disable swipe-back while the forced password-change gate is active.
      return !currentRoute.meta?.overrideBackButton && !IsMustChangePasswordActive.value;
    });

    const allAlerts = computed(() => {
      const alerts = [];
      if (!IsNativePlatform.value && AppUpdateRequired.value) {
        alerts.push(appRefreshAlert);
      }
      if (UserAlerts.value && UserAlerts.value.length > 0) {
        alerts.push(...UserAlerts.value);
      }
      return alerts;
    });

    onBeforeMount(async () => {
      // Order is load-bearing: initializeAppState populates
      // appSettingsStore.ClientNativeVersion via App.getInfo(). That value is
      // consumed by validateCurrentAppBundle to detect a stale live bundle
      // whose stamp exceeds the currently installed native binary. Reversing
      // these two calls silently disables the mismatch check.
      //
      // The finally is load-bearing too. initializeAppState awaits native
      // plugin calls that can reject, and validateCurrentAppBundle is what
      // awaits LiveUpdate.ready() — so letting a rejection here skip it used to
      // leave readiness unclaimed, which the plugin answers by rolling the live
      // bundle back. Readiness is now claimed in main.ts, ahead of mount; this
      // keeps the bundle-compatibility check on every path regardless.
      try {
        await initializeAppState();
      } finally {
        await validateCurrentAppBundle();
      }
    });

    onMounted(async () => {
      initializeBackButton();
    });

    onUnmounted(() => {
      removeAppStateListeners();
    });

    window.oncontextmenu = (e) => {
      e.preventDefault();
    };

    return {
      AppMenuKey,
      RouterOutletKey,
      IsNativePlatform,
      isHeaderVisible,
      isFooterVisible,
      isSwipeGestureEnabled,
      ShowAppFooter,
      UserInfo,
      ShowAppExitAlert,
      currentRoute,
      currentRouteKey,
      allAlerts,
      IsAppFullscreen,
      IsKeyboardOpen,
    };
  },
});
</script>

<style scoped lang="scss">
.app-footer-transparent {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: transparent;
  pointer-events: none;
  :deep(.footer-bar),
  :deep(.app-footer) {
    pointer-events: auto;
  }
}
</style>
